/**
 * Tidio Service - Handles live chat handoff from Guapo AI
 * Tidio is hidden by default and only shown when human agent handoff is needed
 */

// Tidio configuration
const TIDIO_PUBLIC_KEY = 'hcxufkutq4t3rjvlyng6wcteejtbfvgb';
let tidioInitialized = false;

// Phrases that trigger human handoff
const HANDOFF_PHRASES = [
  'human',
  'agent',
  'support',
  'representative',
  'talk to a human',
  'live agent',
  'human agent',
  'real person',
  'speak to someone',
  'customer service',
  'support agent',
  'talk to support',
  'need help from a person',
  'connect me to a human',
  'actual person',
  'speak to a human',
  'talk to someone',
  'live chat',
  'live support',
  'real support'
];

// Phrases indicating bot failure / fallback responses
const FALLBACK_PHRASES = [
  "i don't understand",
  "i'm not sure",
  "could you rephrase",
  "i couldn't find",
  "i don't have information",
  "try again",
  "something went wrong",
  "unable to help with that",
  "outside my expertise",
  "flames flickered"
];

/**
 * Initialize Tidio by loading the script dynamically
 */
export const initializeTidio = () => {
  if (tidioInitialized || typeof window === 'undefined') return;
  
  // Check if already loaded
  if (window.tidioChatApi) {
    tidioInitialized = true;
    window.tidioChatApi.hide();
    return;
  }
  
  // Create and append script
  const script = document.createElement('script');
  script.src = `//code.tidio.co/${TIDIO_PUBLIC_KEY}.js`;
  script.async = true;
  
  // Hide widget when loaded
  document.addEventListener('tidioChat-ready', function() {
    if (window.tidioChatApi) {
      window.tidioChatApi.hide();
      tidioInitialized = true;
    }
  });
  
  document.body.appendChild(script);
};

// Check if Tidio API is available
const isTidioReady = () => {
  return typeof window !== 'undefined' && window.tidioChatApi;
};

// Wait for Tidio to be ready
const waitForTidio = (timeout = 10000) => {
  return new Promise((resolve, reject) => {
    if (isTidioReady()) {
      resolve(window.tidioChatApi);
      return;
    }
    
    // Initialize if not done yet
    initializeTidio();
    
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isTidioReady()) {
        clearInterval(checkInterval);
        resolve(window.tidioChatApi);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(new Error('Tidio not available'));
      }
    }, 200);
  });
};

/**
 * Set user contact properties in Tidio (called on login)
 */
export const setTidioUserContext = async (user) => {
  try {
    const tidio = await waitForTidio();
    if (user && tidio.setContactProperties) {
      tidio.setContactProperties({
        name: user.name || 'GuapGoals User',
        email: user.email || '',
        distinct_id: user.user_id || ''
      });
    }
  } catch (error) {
    console.warn('Tidio not available for user context:', error.message);
  }
};

/**
 * Check if user message explicitly requests human agent
 */
export const isHumanHandoffRequest = (message) => {
  const lowerMessage = message.toLowerCase();
  return HANDOFF_PHRASES.some(phrase => lowerMessage.includes(phrase));
};

/**
 * Check if bot response is a fallback/failure response
 */
export const isFallbackResponse = (response) => {
  const lowerResponse = response.toLowerCase();
  return FALLBACK_PHRASES.some(phrase => lowerResponse.includes(phrase));
};

/**
 * Check for frustration signals in user message
 * - ALL CAPS (more than 3 words)
 * - Angry keywords
 * - Excessive punctuation
 */
export const detectFrustration = (message) => {
  // Check for ALL CAPS (more than 3 consecutive words)
  const words = message.split(/\s+/);
  const capsWords = words.filter(w => w.length > 2 && w === w.toUpperCase() && /[A-Z]/.test(w));
  if (capsWords.length >= 3) return true;
  
  // Check for angry keywords
  const angryKeywords = [
    'terrible', 'awful', 'horrible', 'useless', 'stupid', 'dumb', 'broken',
    'waste of time', 'frustrated', 'angry', 'annoyed', 'ridiculous', 'pathetic',
    'worst', 'hate', 'sucks', 'garbage', 'trash', 'scam', 'bs', 'wtf'
  ];
  const lowerMessage = message.toLowerCase();
  if (angryKeywords.some(keyword => lowerMessage.includes(keyword))) return true;
  
  // Check for excessive punctuation (more than 3 ! or ?)
  const exclamations = (message.match(/[!?]/g) || []).length;
  if (exclamations >= 4) return true;
  
  return false;
};

/**
 * Format chat transcript for Tidio agent
 */
export const formatTranscriptForAgent = (messages) => {
  return messages.map(msg => {
    const role = msg.role === 'user' ? 'User' : 'Guapo';
    const time = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : '';
    return `[${time}] ${role}: ${msg.content}`;
  }).join('\n');
};

/**
 * Pass chat transcript to Tidio and open live chat
 */
export const initiateHumanHandoff = async (messages, onClose) => {
  try {
    console.log('Initiating Tidio handoff...');
    
    // Ensure Tidio is initialized
    initializeTidio();
    
    // Wait for Tidio with longer timeout
    const tidio = await waitForTidio(15000);
    
    console.log('Tidio API available:', !!tidio);
    
    // Format the transcript
    const transcript = formatTranscriptForAgent(messages);
    
    // Pass context to Tidio
    if (tidio.setContactProperties) {
      tidio.setContactProperties({
        guapo_chat_history: transcript,
        handoff_reason: 'User requested human agent or bot unable to assist',
        handoff_time: new Date().toISOString()
      });
      console.log('Contact properties set');
    }
    
    // Close Guapo chat window first
    if (onClose) {
      onClose();
    }
    
    // Show and open Tidio with a small delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Use display(true) for better mobile support
    tidio.display(true);
    console.log('Tidio displayed');
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    tidio.open();
    console.log('Tidio opened');
    
    return true;
  } catch (error) {
    console.error('Failed to initiate Tidio handoff:', error);
    
    // Try one more time with direct window access
    try {
      if (window.tidioChatApi) {
        window.tidioChatApi.display(true);
        window.tidioChatApi.open();
        if (onClose) onClose();
        return true;
      }
    } catch (e) {
      console.error('Fallback Tidio attempt failed:', e);
    }
    
    return false;
  }
};

/**
 * Hide Tidio widget (called on logout or when not needed)
 */
export const hideTidio = async () => {
  try {
    if (window.tidioChatApi) {
      window.tidioChatApi.hide();
    }
  } catch (error) {
    // Tidio not available, that's fine
  }
};

/**
 * Setup Tidio close listener - hides widget when user closes Tidio chat
 * This prevents Tidio from blocking Guapo AI
 */
export const setupTidioCloseListener = () => {
  if (typeof window === 'undefined') return;
  
  // Listen for Tidio chat close event
  const handleTidioClose = () => {
    console.log('Tidio chat closed, hiding widget');
    hideTidio();
  };
  
  // Set up listener when Tidio is ready
  document.addEventListener('tidioChat-close', handleTidioClose);
  
  // Also listen for when the chat is closed via API
  if (window.tidioChatApi && window.tidioChatApi.on) {
    window.tidioChatApi.on('close', handleTidioClose);
  }
  
  return () => {
    document.removeEventListener('tidioChat-close', handleTidioClose);
  };
};

/**
 * Check if Tidio is currently visible/open
 */
export const isTidioVisible = () => {
  if (typeof window === 'undefined' || !window.tidioChatApi) return false;
  try {
    return window.tidioChatApi.isOpen?.() || false;
  } catch {
    return false;
  }
};

export default {
  initializeTidio,
  setTidioUserContext,
  isHumanHandoffRequest,
  isFallbackResponse,
  detectFrustration,
  formatTranscriptForAgent,
  initiateHumanHandoff,
  hideTidio,
  setupTidioCloseListener,
  isTidioVisible
};
